import { useState, useEffect, useRef } from 'react';
import { useHiveCore } from '../hivecore-scene-stream/HiveCoreProvider';

type WidgetState = 'idle' | 'pressed' | 'pending' | 'success' | 'error' | 'disabled';

interface ResultCallback {
  resolve: () => void;
  reject: (error: Error) => void;
}

interface PendingInfo {
  startTime: number;
  debounceTimer?: NodeJS.Timeout;
}

export function useActionFeedback() {
  const { sendIntent } = useHiveCore();
  const [states, setStates] = useState<Record<string, WidgetState>>({});
  const [showPending, setShowPending] = useState<Record<string, boolean>>({});
  const resultCallbacks = useRef<Map<string, ResultCallback>>(new Map());
  const cidToWidget = useRef<Map<string, string>>(new Map());
  const pendingInfo = useRef<Map<string, PendingInfo>>(new Map());

  function setState(key: string, state: WidgetState) {
    setStates(prev => ({ ...prev, [key]: state }));
  }

  function setShowPendingIndicator(key: string, show: boolean) {
    setShowPending(prev => ({ ...prev, [key]: show }));
  }

  function fire(widgetKey: string, name: string, args?: any): Promise<void> {
    const startTime = Date.now();
    setState(widgetKey, 'pending');
    
    const debounceTimer = setTimeout(() => {
      if (states[widgetKey] === 'pending') {
        setShowPendingIndicator(widgetKey, true);
      }
    }, 250);
    
    pendingInfo.current.set(widgetKey, { startTime, debounceTimer });
    
    const cid = sendIntent(name, args || {}, widgetKey);
    
    if (!cid) {
      clearTimeout(debounceTimer);
      pendingInfo.current.delete(widgetKey);
      setShowPendingIndicator(widgetKey, false);
      setState(widgetKey, 'error');
      setTimeout(() => setState(widgetKey, 'idle'), 300);
      return Promise.reject(new Error('Failed to send intent: not connected'));
    }

    cidToWidget.current.set(cid, widgetKey);

    return new Promise<void>((resolve, reject) => {
      resultCallbacks.current.set(cid, { resolve, reject });
      
      setTimeout(() => {
        if (resultCallbacks.current.has(cid)) {
          resultCallbacks.current.delete(cid);
          cidToWidget.current.delete(cid);
          const info = pendingInfo.current.get(widgetKey);
          if (info?.debounceTimer) {
            clearTimeout(info.debounceTimer);
          }
          pendingInfo.current.delete(widgetKey);
          setShowPendingIndicator(widgetKey, false);
          setState(widgetKey, 'error');
          setTimeout(() => setState(widgetKey, 'idle'), 300);
          reject(new Error('Intent timeout'));
        }
      }, 10000);
    });
  }

  function handleResult(cid: string, ok: boolean) {
    const widgetKey = cidToWidget.current.get(cid);
    const callback = resultCallbacks.current.get(cid);
    
    if (widgetKey) {
      const info = pendingInfo.current.get(widgetKey);
      if (info?.debounceTimer) {
        clearTimeout(info.debounceTimer);
      }
      
      const latency = info ? Date.now() - info.startTime : 0;
      
      if (ok && latency > 600) {
        setState(widgetKey, 'success');
        setTimeout(() => setState(widgetKey, 'idle'), 200);
      } else if (!ok) {
        setState(widgetKey, 'error');
        setTimeout(() => setState(widgetKey, 'idle'), 300);
      } else {
        setState(widgetKey, 'idle');
      }
      
      setShowPendingIndicator(widgetKey, false);
      pendingInfo.current.delete(widgetKey);
      cidToWidget.current.delete(cid);
    }
    
    if (callback) {
      if (ok) {
        callback.resolve();
      } else {
        callback.reject(new Error('Intent failed'));
      }
      resultCallbacks.current.delete(cid);
    }
  }

  useEffect(() => {
    (window as any).__handleActionResult = handleResult;
    return () => {
      delete (window as any).__handleActionResult;
    };
  }, []);

  return { 
    state: states,
    showPending,
    fire,
    setState 
  };
}
